/**
 * The chapter timeline (spec §2.1) — 200 px of "where am I, and how much is
 * left", visible on every screen above 900 px.
 *
 * Four chapters; the one you are in expands to its decisions. A step you have
 * passed carries a check (a 120 ms scale pop, skipped under
 * `prefers-reduced-motion`); a step the study prefilled carries a small "from
 * your reel" dot; anything at or below `furthest` is clickable. The rail sits
 * AFTER the step content in the DOM (the grid puts it left with `order`), so
 * Tab reaches the control before the navigation.
 */
import { useEffect, useRef, useState } from "react";
import { Check, MoreHorizontal, X } from "lucide-react";
import { CHAPTERS, type StepDef, type StepId } from "../../lib/brandSetup/steps";
import { MONO_LABEL } from "./ui";

const POP_CSS = `
@keyframes ql-check-pop { from { transform: scale(.55); opacity: .2 } to { transform: scale(1); opacity: 1 } }
.ql-check-pop { animation: ql-check-pop 120ms var(--ql-ease) both; }
@media (prefers-reduced-motion: reduce) { .ql-check-pop { animation: none; } }
`;

export interface TimelineRailProps {
  /** This lane's visible steps, in walk order. */
  steps: StepDef[];
  current: StepId;
  /** The furthest step reached — anything at or before it is clickable. */
  furthest: StepId;
  onGo: (id: StepId) => void;
  /** "Step n of N", or null on an uncounted screen. */
  counter: { n: number; total: number } | null;
  onExit: () => void;
  onStartOver: () => void;
  /** Steps the brand study prefilled — they carry a "from your reel" dot. */
  studied: Set<StepId>;
  /**
   * Steps that still WANT something even though the walk has passed them —
   * a refresh drops the uploaded logo and font bytes (spec §2.6), and a check
   * mark over a step that lost its answer would be a lie.
   */
  undone?: Set<StepId>;
  /** One sentence under the counter (the draft-quota failure, §2.6). */
  notice?: string | null;
}

export function TimelineRail(p: TimelineRailProps): React.JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent): void => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const index = (id: StepId): number => p.steps.findIndex((d) => d.id === id);
  const currentIdx = index(p.current);
  // A `furthest` this lane does not list (a lane change hid it) must not lock
  // the whole rail: an unknown one reads as past-the-end, never as before-all.
  const rawFurthest = index(p.furthest);
  const furthestIdx = rawFurthest < 0 ? p.steps.length - 1 : rawFurthest;
  const reachable = (id: StepId): boolean => index(id) <= furthestIdx;
  // A step you have ANSWERED stays checked, even when you walk back to change
  // something earlier — and a resumed draft opens with its rail already
  // ticked (spec §2.2, welcome › cases).
  const answeredIdx = Math.max(currentIdx, furthestIdx);
  const barPct = p.counter
    ? (p.counter.n / p.counter.total) * 100
    : p.steps.length > 1 && currentIdx > 0
      ? (currentIdx / (p.steps.length - 1)) * 100
      : 0;

  return (
    <nav aria-label="Brand setup steps" style={{ display: "grid", gap: 18, alignContent: "start" }}>
      <style>{POP_CSS}</style>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span aria-hidden style={{ color: "var(--ql-yellow)", fontSize: 15, lineHeight: 1 }}>◆</span>
        <button
          type="button"
          onClick={p.onExit}
          title="Leave setup — we keep what you've done"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            border: "none",
            background: "transparent",
            color: "var(--ql-fg-subtle)",
            fontSize: 12.5,
            fontFamily: "inherit",
            cursor: "pointer",
            padding: 2,
          }}
        >
          <X size={12} />
          Exit
        </button>
        <div ref={menuRef} style={{ position: "relative", marginLeft: "auto" }}>
          <button
            type="button"
            aria-label="More"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            style={{
              border: "none",
              background: "transparent",
              color: "var(--ql-fg-subtle)",
              cursor: "pointer",
              display: "inline-flex",
              padding: 2,
            }}
          >
            <MoreHorizontal size={15} />
          </button>
          {menuOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                left: 0,
                zIndex: 30,
                minWidth: 150,
                padding: 6,
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,.10)",
                background: "rgba(16,16,19,.98)",
                boxShadow: "0 12px 30px rgba(0,0,0,.5)",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  p.onStartOver();
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 10px",
                  borderRadius: 9,
                  border: "none",
                  background: "transparent",
                  color: "var(--ql-fg)",
                  fontSize: 13,
                  fontFamily: "inherit",
                  cursor: "pointer",
                }}
              >
                Start over
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gap: 2 }}>
        {CHAPTERS.map((ch) => {
          const chapterSteps = p.steps.filter((d) => d.chapter === ch.id);
          if (chapterSteps.length === 0) return null;
          const isCurrent = chapterSteps.some((d) => d.id === p.current);
          const done =
            !isCurrent && chapterSteps.every((d) => index(d.id) < answeredIdx && !p.undone?.has(d.id));
          const first = chapterSteps[0]!;
          const canOpen = reachable(first.id);
          return (
            <div key={ch.id} style={{ display: "grid", gap: 2 }}>
              <button
                type="button"
                disabled={!canOpen || isCurrent}
                onClick={() => canOpen && !isCurrent && p.onGo(first.id)}
                aria-current={isCurrent ? "step" : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 10px",
                  borderRadius: 11,
                  border: "none",
                  background: isCurrent ? "rgba(255,213,0,.07)" : "transparent",
                  color: isCurrent ? "var(--ql-fg)" : canOpen ? "var(--ql-fg-muted)" : "var(--ql-fg-faint)",
                  cursor: canOpen && !isCurrent ? "pointer" : "default",
                  fontFamily: "inherit",
                  textAlign: "left",
                  width: "100%",
                }}
              >
                <Mark done={done} current={isCurrent} size={20} />
                <span style={{ fontSize: 13.5, fontWeight: isCurrent ? 700 : 500 }}>{ch.label}</span>
              </button>

              {isCurrent &&
                chapterSteps
                  .filter((d) => d.counted)
                  .map((d) => {
                    const idx = index(d.id);
                    const stepDone = idx < answeredIdx && d.id !== p.current && !p.undone?.has(d.id);
                    const active = d.id === p.current;
                    const canGo = reachable(d.id) && !active;
                    return (
                      <button
                        key={d.id}
                        type="button"
                        disabled={!canGo}
                        onClick={() => canGo && p.onGo(d.id)}
                        aria-current={active ? "step" : undefined}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 9,
                          padding: "6px 10px 6px 22px",
                          borderRadius: 10,
                          border: "none",
                          background: "transparent",
                          color: active ? "var(--ql-yellow)" : reachable(d.id) ? "var(--ql-fg-muted)" : "var(--ql-fg-faint)",
                          cursor: canGo ? "pointer" : "default",
                          fontFamily: "inherit",
                          textAlign: "left",
                          width: "100%",
                        }}
                      >
                        <Mark done={stepDone} current={active} size={16} />
                        <span style={{ fontSize: 12.5, fontWeight: active ? 700 : 500 }}>{d.label}</span>
                        {p.studied.has(d.id) && (
                          <span
                            title="from your reel"
                            aria-label="from your reel"
                            style={{
                              width: 5,
                              height: 5,
                              borderRadius: 999,
                              background: "var(--ql-yellow)",
                              marginLeft: "auto",
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </button>
                    );
                  })}
            </div>
          );
        })}
      </div>

      <div style={{ display: "grid", gap: 7 }}>
        {p.counter && <div style={MONO_LABEL}>Step {p.counter.n} of {p.counter.total}</div>}
        <div style={{ height: 4, borderRadius: 999, background: "rgba(255,255,255,.07)", overflow: "hidden" }}>
          {/* An UNCOUNTED screen (reading) has no "step n of N" — but it is
              still mid-walk, so the bar keeps the position it reached rather
              than snapping back to zero. */}
          <div
            style={{
              height: "100%",
              width: `${barPct}%`,
              background: "var(--ql-yellow)",
              transition: "width .3s var(--ql-ease)",
            }}
          />
        </div>
        {p.notice && (
          <div style={{ fontSize: 12, color: "var(--ql-fg-subtle)", lineHeight: 1.5 }}>{p.notice}</div>
        )}
      </div>
    </nav>
  );
}

function Mark({ done, current, size }: { done: boolean; current: boolean; size: number }): React.JSX.Element {
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: current ? "1.5px solid var(--ql-yellow)" : "1px solid rgba(255,255,255,.14)",
        background: done ? "rgba(255,213,0,.16)" : "transparent",
        color: "var(--ql-yellow)",
      }}
    >
      {done && <Check className="ql-check-pop" size={Math.round(size * 0.58)} strokeWidth={3} />}
    </span>
  );
}
